import React, { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import SupportModal from '~/components/molecules/SupportModal';
import { useModalContext } from '~/contexts/modal/ModalContext';
import { DropdownMenu } from '~/design-system/components/Dropdown/DropdownMenu';
import { DropdownMenuItem } from '~/design-system/components/Dropdown/DropdownMenuItem';
import { Icon } from '~/design-system/components/Icon/Icon';
import { DesignSystemIconSize } from '~/design-system/components/Icon/utils';
import { UserAvatar } from '~/design-system/components/UserAvatar/UserAvatar';
import { DesignSystemUserAvatarSize } from '~/design-system/components/UserAvatar/utils';
import i18n from '~/i18n/config';
import { getLanguages } from '~/i18n/helpers';
import { useSubscriptionMenuTitle } from '~/modules/settings/hooks/useSubscriptionMenuTitle';
import analytics from '~/services/analytics';
import { useActions } from '~/store/hooks/useActions';
import { useAppSelector } from '~/store/hooks/useAppSelector';

import { getHomePageV2AccountMenuEntries } from '../homePageV2AccountMenuContent';
import styles from '../styles.module.scss';
import { AccountMenuEntry, HomePageV2AccountDropdownProps } from '../types';

export const HomePageV2AccountDropdown = ({
  align = 'end',
  side = 'bottom',
  sideOffset = 8,
  triggerId,
  languageDropdownSide = 'left',
  onNavigate,
}: HomePageV2AccountDropdownProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const profile = useAppSelector((state) => state.profile);
  const { signOut, updateUserFields } = useActions();
  const { handleOpenModal } = useModalContext();
  const { t } = useTranslation(['general', 'home']);
  const getSubscriptionTitle = useSubscriptionMenuTitle();
  const accountMenuEntries = useMemo(
    () => getHomePageV2AccountMenuEntries(t, getSubscriptionTitle),
    [getSubscriptionTitle, t],
  );
  const profileName = profile.name || profile.email || 'JustDone User';

  const handleHelpClick = (): void => {
    analytics.trackEvent('navigation - open help section');

    handleOpenModal({
      onClose: () => analytics.trackEvent('navigation - close support modal'),
      maxWidth: '460px',
      component: ({ onClose }) => <SupportModal onClose={onClose} />,
    });
  };

  const handleLanguageClick = async (language: string): Promise<void> => {
    analytics.trackEvent('settings - chose language', {
      language,
      previous_language: profile.platform_lang || i18n.instance.language || '',
    });

    setIsLanguageMenuOpen(false);
    setIsOpen(false);

    if (profile.platform_lang !== language) {
      i18n.instance.changeLanguage(language);
      await updateUserFields({ platform_lang: language });
    }
  };

  const handleAccountMenuClick = (entry: AccountMenuEntry): void => {
    if (entry.action === 'navigate' && entry.path) {
      onNavigate(entry.path);
      return;
    }

    if (entry.action === 'language') {
      setIsLanguageMenuOpen((isLanguageOpen) => !isLanguageOpen);
      return;
    }

    if (entry.action === 'help') {
      handleHelpClick();
      return;
    }

    signOut();
  };

  return (
    <DropdownMenu
      open={isOpen}
      onOpenChange={(open): void => {
        setIsOpen(open);

        if (!open) {
          setIsLanguageMenuOpen(false);
        }
      }}
      modal={false}
      preventFocusSteal
      side={side}
      align={align}
      sideOffset={sideOffset}
      trigger={
        <button
          type="button"
          data-home-v2-account-trigger={triggerId}
          className={styles.account_trigger}
          aria-label={t('home:v2.account.open_menu', 'Open account menu')}
        >
          <UserAvatar
            name={profileName}
            src={profile.avatar}
            size={DesignSystemUserAvatarSize.MEDIUM}
          />
        </button>
      }
      content={
        <div className={styles.account_menu_items}>
          {accountMenuEntries.map((entry) => (
            <React.Fragment key={`${entry.action}-${entry.path ?? entry.icon}`}>
              {entry.dividerBefore ? (
                <span
                  className={styles.account_menu_divider}
                  role="separator"
                />
              ) : null}
              <div
                className={
                  entry.action === 'language'
                    ? styles.language_dropdown_anchor
                    : undefined
                }
              >
                <DropdownMenuItem
                  StartAdornment={() => (
                    <Icon name={entry.icon} size={DesignSystemIconSize.LARGE} />
                  )}
                  aria-haspopup={
                    entry.action === 'language' ? 'menu' : undefined
                  }
                  aria-expanded={
                    entry.action === 'language' ? isLanguageMenuOpen : undefined
                  }
                  onSelect={(event): void => {
                    if (entry.action === 'language') {
                      event.preventDefault();
                    }
                  }}
                  onClick={(): void => handleAccountMenuClick(entry)}
                >
                  {entry.label}
                </DropdownMenuItem>
                {entry.action === 'language' && isLanguageMenuOpen ? (
                  <div
                    data-home-v2-language-dropdown
                    className={[
                      styles.language_dropdown,
                      languageDropdownSide === 'right'
                        ? styles.language_dropdown_open_right
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    role="menu"
                    aria-label={t('general:app_language', 'App Language')}
                  >
                    {getLanguages().map((language) => (
                      <DropdownMenuItem
                        key={language.value}
                        isActive={profile.platform_lang === language.value}
                        onSelect={(event): void => {
                          event.preventDefault();
                        }}
                        onClick={(): void => {
                          handleLanguageClick(language.value);
                        }}
                      >
                        {language.getText(t)}
                      </DropdownMenuItem>
                    ))}
                  </div>
                ) : null}
              </div>
            </React.Fragment>
          ))}
        </div>
      }
      contentClassName={styles.account_dropdown_menu}
    />
  );
};
